import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, Modal, Spin } from "antd";
import { FiClock, FiImage, FiLock, FiPlayCircle, FiUploadCloud } from "react-icons/fi";
import { toast } from "react-toastify";
import { BaseUrl } from "../shared/BaseUrl";
import "./MyGallery.css";

const MyGallery = () => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [hasQr, setHasQr] = useState(true);
  const [qrId, setQrId] = useState("");
  const [contents, setContents] = useState([]);
  const [currentContentId, setCurrentContentId] = useState("");
  const [settingContentId, setSettingContentId] = useState("");
  const [previewContent, setPreviewContent] = useState(null);
  const [loadedCardImages, setLoadedCardImages] = useState({});
  const [failedCardImages, setFailedCardImages] = useState({});

  const getApiErrorMessage = (error, fallbackMessage) => {
    return error?.response?.data?.message || error?.message || fallbackMessage;
  };

  const fetchGallery = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const qrResponse = await axios.get(`${BaseUrl}/api/qrcode/user/qr`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const resolvedQrId = qrResponse?.data?._id || "";
      setQrId(resolvedQrId);
      setHasQr(Boolean(resolvedQrId));

      if (!resolvedQrId) {
        setContents([]);
        setCurrentContentId("");
        return;
      }

      const contentsResponse = await axios.get(
        `${BaseUrl}/api/content/contents/${resolvedQrId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setContents(contentsResponse?.data?.contents || []);
      setCurrentContentId(contentsResponse?.data?.currentContent?._id || "");
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400 || status === 404) {
        setHasQr(false);
        setContents([]);
        setCurrentContentId("");
      } else {
        toast.error(getApiErrorMessage(error, "Unable to load your gallery."), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  useEffect(() => {
    const validIds = new Set(contents.map((item) => String(item._id)));
    setLoadedCardImages((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => validIds.has(id))
      )
    );
    setFailedCardImages((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => validIds.has(id))
      )
    );
  }, [contents]);

  const handleSetCurrent = async (contentId) => {
    if (!qrId || !contentId) {
      return;
    }

    setSettingContentId(contentId);
    try {
      const response = await axios.post(
        `${BaseUrl}/api/content/set/${contentId}`,
        { qrId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCurrentContentId(contentId);
      toast.success(response?.data?.message || "Current content updated", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to set this item as current content."),
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setSettingContentId("");
    }
  };

  const sortedContents = useMemo(() => {
    return [...contents].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [contents]);

  const formatDate = (value) => {
    if (!value) {
      return "Unknown date";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "Unknown date";
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPrimaryLabel = (item) => {
    if (item.label && item.label.trim()) {
      return item.label.trim();
    }
    if (item.text && item.text.trim()) {
      return item.text.trim().slice(0, 90);
    }
    return "Untitled Content";
  };

  const markCardImageLoaded = (contentId) => {
    if (!contentId) {
      return;
    }
    setLoadedCardImages((prev) => ({ ...prev, [contentId]: true }));
    setFailedCardImages((prev) => ({ ...prev, [contentId]: false }));
  };

  const markCardImageFailed = (contentId) => {
    if (!contentId) {
      return;
    }
    setFailedCardImages((prev) => ({ ...prev, [contentId]: true }));
    setLoadedCardImages((prev) => ({ ...prev, [contentId]: false }));
  };

  const renderCardPreview = (item) => {
    if (item.contentType === "image" && item.contentUrl) {
      const itemId = String(item._id || "");
      const isLoaded = Boolean(loadedCardImages[itemId]);
      const isFailed = Boolean(failedCardImages[itemId]);

      return (
        <div className={`gallery-card-media-frame ${isLoaded ? "is-loaded" : ""}`}>
          {!isLoaded && !isFailed ? (
            <div className="gallery-card-media-skeleton">
              <Spin size="small" />
            </div>
          ) : null}
          {isFailed ? (
            <div className="gallery-card-media shell">
              <FiImage />
              <span>Image unavailable</span>
            </div>
          ) : (
            <img
              src={item.contentUrl}
              alt={getPrimaryLabel(item)}
              className="gallery-card-image"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              onLoad={() => markCardImageLoaded(itemId)}
              onError={() => markCardImageFailed(itemId)}
            />
          )}
        </div>
      );
    }

    if (item.contentType === "video" && item.contentUrl) {
      return (
        <div className="gallery-card-media shell">
          <FiPlayCircle />
          <span>Video Content</span>
        </div>
      );
    }

    if (item.contentType === "audio" && item.contentUrl) {
      return (
        <div className="gallery-card-media shell">
          <FiUploadCloud />
          <span>Audio Content</span>
        </div>
      );
    }

    if (item.contentType === "text") {
      return (
        <div className="gallery-card-media text">
          <p>{item.text || "Text content"}</p>
        </div>
      );
    }

    return (
      <div className="gallery-card-media shell">
        <FiImage />
        <span>File Content</span>
      </div>
    );
  };

  const renderPreviewBody = (item) => {
    if (!item) {
      return null;
    }

    if (item.contentType === "image" && item.contentUrl) {
      return <img src={item.contentUrl} alt={getPrimaryLabel(item)} className="gallery-preview-image" />;
    }
    if (item.contentType === "video" && item.contentUrl) {
      return (
        <video controls className="gallery-preview-video">
          <source src={item.contentUrl} />
          Your browser does not support video playback.
        </video>
      );
    }
    if (item.contentType === "audio" && item.contentUrl) {
      return (
        <audio controls className="gallery-preview-audio">
          <source src={item.contentUrl} />
          Your browser does not support audio playback.
        </audio>
      );
    }
    if (item.contentType === "text") {
      return <pre className="gallery-preview-text">{item.text || "No text content available."}</pre>;
    }
    if (item.contentUrl) {
      return (
        <div className="gallery-preview-fallback">
          <p>Inline preview is not available for this file type.</p>
          <a href={item.contentUrl} target="_blank" rel="noopener noreferrer">
            Open content in a new tab
          </a>
        </div>
      );
    }
    return <p className="gallery-preview-empty">No preview available.</p>;
  };

  if (loading) {
    return (
      <section className="gallery-shell loading">
        <Spin tip="Loading your gallery..." />
      </section>
    );
  }

  if (!hasQr) {
    return (
      <section className="gallery-shell empty">
        <h2>Your Gallery Is Ready</h2>
        <p>Create your QR first to start collecting and managing gallery content.</p>
      </section>
    );
  }

  return (
    <section className="gallery-shell">
      <header className="gallery-header">
        <div>
          <h2>My Gallery</h2>
          <p>All uploaded content appears here. Choose any item and set it live when needed.</p>
        </div>
        <span className="gallery-total">{sortedContents.length} items</span>
      </header>

      {sortedContents.length === 0 ? (
        <div className="gallery-empty-content">
          <FiUploadCloud />
          <p>No content in your gallery yet. Add your first file or text content.</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {sortedContents.map((item) => {
            const isCurrent = item._id === currentContentId;
            return (
              <Card key={item._id} className={`gallery-card ${isCurrent ? "active" : ""}`}>
                {renderCardPreview(item)}
                <div className="gallery-card-body">
                  <h3>{getPrimaryLabel(item)}</h3>
                  <div className="gallery-meta-row">
                    <span>{item.contentType || "content"}</span>
                    {item.category ? <span>{item.category}</span> : null}
                  </div>
                  <div className="gallery-meta-row">
                    <span className="with-icon">
                      <FiClock />
                      {formatDate(item.createdAt)}
                    </span>
                    {item.isSecure ? (
                      <span className="with-icon secure">
                        <FiLock />
                        Secure
                      </span>
                    ) : null}
                  </div>
                  <div className="gallery-actions">
                    <button
                      type="button"
                      className="gallery-btn secondary"
                      onClick={() => setPreviewContent(item)}
                    >
                      Preview
                    </button>
                    {isCurrent ? (
                      <span className="gallery-live-pill">Live Now</span>
                    ) : (
                      <button
                        type="button"
                        className="gallery-btn primary"
                        onClick={() => handleSetCurrent(item._id)}
                        disabled={settingContentId === item._id}
                      >
                        {settingContentId === item._id ? "Setting..." : "Set As Current"}
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        title={previewContent ? getPrimaryLabel(previewContent) : "Preview"}
        open={Boolean(previewContent)}
        onCancel={() => setPreviewContent(null)}
        footer={null}
        width={860}
        destroyOnClose
      >
        {renderPreviewBody(previewContent)}
      </Modal>
    </section>
  );
};

export default MyGallery;
